import {
  Box,
  IconButton,
  Paper as MuiPaper,
  Stack,
  SwipeableDrawer,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

export const BOTTOM_NAV_HEIGHT = 69;

export const Root = styled(MuiPaper)(({ theme }) => ({
  position: "sticky",
  bottom: "0",
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2.5),
  padding: theme.spacing(2, 2.5),
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  zIndex: 1100,
  background: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: `0 -6px 16px ${alpha(
    theme.palette.common.black,
    theme.palette.mode === "dark" ? 0.35 : 0.06
  )}`,
  boxSizing: "border-box",

  [theme.breakpoints.down(1250)]: {
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

export const CollapsedBar = styled(MuiPaper)(({ theme }) => ({
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1100,
  boxSizing: "border-box",
  padding: `${theme.spacing(1.5)} 0 ${theme.spacing(1)}`,
  background: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  touchAction: "none",
  cursor: "pointer",

  "& > *": {
    pointerEvents: "none",
  },

  [theme.breakpoints.down(900)]: {
    bottom: `${BOTTOM_NAV_HEIGHT}px`,
  },
}));

export const MobileDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  pointerEvents: "none",

  [theme.breakpoints.down(900)]: {
    clipPath: `inset(0 0 ${BOTTOM_NAV_HEIGHT}px 0)`,
  },

  "& .MuiDrawer-paper": {
    pointerEvents: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    background: theme.palette.background.paper,
    padding: `${theme.spacing(1.5)} 0 ${theme.spacing(1)}`,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    touchAction: "none",

    [theme.breakpoints.down(900)]: {
      bottom: `${BOTTOM_NAV_HEIGHT}px`,
    },
  },
}));

export const CountdownSlot = styled(Box)({
  flexShrink: 0,
  display: "flex",

  "&:empty": {
    display: "none",
  },
});

export const TopWrapper = styled(Stack)(({ theme }) => ({
  flex: "1 1 0",
  minWidth: 0,
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(2),

  [theme.breakpoints.down(1250)]: {
    width: "100%",
    flex: "none",
  },
}));

export const TradeCardsRow = styled(Box)(({ theme }) => ({
  flex: "1 1 0",
  minWidth: 0,

  "& .CardsCarouselArrow": {
    opacity: 0.5,
  },

  [theme.breakpoints.down(1250)]: {
    width: "100%",
    flex: "none",
  },
}));

export const CollapseToggle = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  width: 24,
  height: 20,
  marginTop: "2px",
  padding: 0,
  borderRadius: "10px",
  color: theme.palette.text.secondary,

  "& svg": {
    width: 18,
    height: 18,
  },
}));

export const QuestionLine = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

export const CollapsibleContent = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export const MobileTopSection = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "column",
  gap: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

export const SliderRow = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "$hasArrows",
})<{ $hasArrows: boolean }>(({ theme, $hasArrows }) => ({
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
  gap: theme.spacing(1),
  paddingLeft: theme.spacing($hasArrows ? 1 : 2),
  paddingRight: theme.spacing($hasArrows ? 1 : 2),
}));

export const SliderViewport = styled(Stack)({
  flex: 1,
  minWidth: 0,
});

export const SliderArrow = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  width: 20,
  height: 20,
  padding: 0,
  borderRadius: "20px",
  border: `1px solid ${theme.palette.grey.A400}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  "& svg": {
    width: 15,
    height: 15,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const SliderDots = styled(Stack)({
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  gap: "6px",
  paddingBottom: "4px",
});

export const SliderDot = styled("button", {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active: boolean }>(({ theme, $active }) => ({
  width: $active ? 16 : 6,
  height: 6,
  padding: 0,
  border: "none",
  borderRadius: $active ? "3px" : "50%",
  cursor: "pointer",
  flexShrink: 0,
  backgroundColor: $active
    ? theme.palette.text.primary
    : theme.palette.grey.A400,
  transition: theme.transitions.create(["background-color", "width", "border-radius"], {
    duration: theme.transitions.duration.shorter,
  }),
}));
